// Sections de la fiche d'une session (lot L5-B) : feuille de présence et quorum, bureau de séance, ordre du
// jour et délibérations (votes, report, suivi d'exécution), procès-verbal et validations, transmission.
// Les actes dépendent de l'état et des droits ; le serveur refait tous les contrôles.
import { CHOIX_VOTE, MODES_TRANSMISSION, STATUTS_PRESENCE, type SessionConseil, type StatutPresence } from '@cc/contrats';
import { Bouton, Lien, PuceEtat, Selection } from '@cc/ui';
import { useState } from 'react';
import { useAction } from '../../commun/useAction';
import { designerBureau, INVALIDER_CONSEILS, saisirPresences, validerPv } from './api';
import { libelleDeliberation, texteQuorum, TON_DELIBERATION, type DroitsConseils } from './logique';
import styles from '../achats/achats.module.css';

export function SectionPresence({ s, droits }: { s: SessionConseil; droits: DroitsConseils }) {
  const modifiable = s.etat === 'ouverte' && droits.modifier;
  const [feuille, setFeuille] = useState<Record<string, { statut: StatutPresence; mandataire: string }>>(
    Object.fromEntries(s.membres.map((m) => [m.mandat_id, { statut: m.statut ?? 'absent', mandataire: m.mandataire?.mandat_id ?? '' }])),
  );
  const action = useAction({
    executer: () => saisirPresences(s.id, { presences: s.membres.map((m) => ({ mandat_id: m.mandat_id, statut: feuille[m.mandat_id]!.statut, mandataire_mandat_id: feuille[m.mandat_id]!.statut === 'represente' ? feuille[m.mandat_id]!.mandataire || undefined : undefined })) }),
    succes: (r) => `Feuille de présence enregistrée : ${texteQuorum(r.quorum)}.`,
    invalider: INVALIDER_CONSEILS,
  });
  const presents = s.membres.filter((m) => feuille[m.mandat_id]?.statut === 'present');
  return (
    <section className={styles.bloc} aria-labelledby="titre-presence">
      <h2 id="titre-presence">Présences et quorum</h2>
      <p className={styles.detail} data-testid="quorum">{texteQuorum(s.quorum)}</p>
      {s.membres.length === 0 ? <p className={styles.detail}>Les membres en exercice sont fixés à la convocation.</p> : (
        <ul className={styles.liste} aria-label="Feuille de présence">
          {s.membres.map((m) => (
            <li key={m.mandat_id}>
              {modifiable ? (
                <div className={styles.grille}>
                  <Selection libelle={`Présence de ${m.nom_complet}`} options={Object.entries(STATUTS_PRESENCE).map(([valeur, libelle]) => ({ valeur, libelle }))} value={feuille[m.mandat_id]?.statut} onChange={(e) => setFeuille((x) => ({ ...x, [m.mandat_id]: { statut: e.target.value as StatutPresence, mandataire: x[m.mandat_id]?.mandataire ?? '' } }))} />
                  {feuille[m.mandat_id]?.statut === 'represente' ? (
                    <Selection libelle={`Mandataire de ${m.nom_complet}`} optionVide="Choisir un élu présent" options={presents.filter((p) => p.mandat_id !== m.mandat_id).map((p) => ({ valeur: p.mandat_id, libelle: p.nom_complet }))} value={feuille[m.mandat_id]?.mandataire} onChange={(e) => setFeuille((x) => ({ ...x, [m.mandat_id]: { statut: 'represente', mandataire: e.target.value } }))} />
                  ) : null}
                </div>
              ) : (
                <span>{m.nom_complet} : {m.statut ? STATUTS_PRESENCE[m.statut] : 'non renseigné'}{m.mandataire ? ` par ${m.mandataire.nom_complet}` : ''}</span>
              )}
            </li>
          ))}
        </ul>
      )}
      {modifiable ? <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Enregistrer la feuille de présence</Bouton> : null}
      {action.messageErreur ? <p role="alert" className={styles.negatif}>{action.messageErreur}</p> : null}
    </section>
  );
}

export function SectionBureau({ s, droits }: { s: SessionConseil; droits: DroitsConseils }) {
  const [v, setV] = useState({ president: s.bureau.president?.mandat_id ?? s.membres.find((m) => m.fonction === 'president')?.mandat_id ?? '', secretaire: s.bureau.secretaire?.mandat_id ?? '' });
  const action = useAction({ executer: () => designerBureau(s.id, v.president, v.secretaire), succes: 'Bureau de séance désigné.', invalider: INVALIDER_CONSEILS });
  const modifiable = ['convoquee', 'ouverte', 'tenue'].includes(s.etat) && droits.modifier;
  const options = s.membres.map((m) => ({ valeur: m.mandat_id, libelle: m.nom_complet }));
  return (
    <section className={styles.bloc} aria-labelledby="titre-bureau">
      <h2 id="titre-bureau">Bureau de séance</h2>
      <p className={styles.detail}>Président : {s.bureau.president?.nom_complet ?? 'non désigné'} ; secrétaire : {s.bureau.secretaire?.nom_complet ?? 'non désigné'}.</p>
      {modifiable && options.length > 0 ? (
        <div className={styles.grille}>
          <Selection libelle="Président de séance" optionVide="Choisir" options={options} value={v.president} onChange={(e) => setV((x) => ({ ...x, president: e.target.value }))} />
          <Selection libelle="Secrétaire de séance" optionVide="Choisir" options={options} value={v.secretaire} onChange={(e) => setV((x) => ({ ...x, secretaire: e.target.value }))} />
          <Bouton variante="secondaire" chargement={action.enCours} onClick={() => action.lancer(undefined)}>Désigner le bureau</Bouton>
        </div>
      ) : null}
      {action.messageErreur ? <p role="alert" className={styles.negatif}>{action.messageErreur}</p> : null}
    </section>
  );
}

export type ActeDeliberation = { type: 'vote' | 'report' | 'lien' | 'execution'; id: string; objet: string };

export function SectionOrdreDuJour({ s, droits, onActe }: { s: SessionConseil; droits: DroitsConseils; onActe: (a: ActeDeliberation) => void }) {
  return (
    <section className={styles.bloc} aria-labelledby="titre-odj">
      <h2 id="titre-odj">Ordre du jour et délibérations</h2>
      {s.points.length === 0 ? <p className={styles.detail}>Aucun point inscrit.</p> : (
        <ol className={styles.liste}>
          {s.points.map((p) => {
            const d = p.deliberation;
            return (
              <li key={p.id} aria-label={`Point ${p.rang} : ${p.intitule}`}>
                <h3>{`${p.rang}. ${p.intitule}`}</h3>
                {p.rapporteur ? <p className={styles.detail}>Rapporteur : {p.rapporteur}</p> : null}
                {p.expose ? <p className={styles.detail}>{p.expose}</p> : null}
                {p.documents.length > 0 ? <p className={styles.detail}>Documents de séance : {p.documents.map((doc, i) => <span key={doc.id}>{i > 0 ? ', ' : ''}<Lien href={`/documents/${doc.id}`}>{doc.titre}</Lien></span>)}</p> : null}
                {p.note_interne ? <p className={styles.detail}>Note interne (non publiée) : {p.note_interne}</p> : null}
                {d ? (
                  <div>
                    <p className={styles.actesLigne}>
                      <PuceEtat ton={TON_DELIBERATION[d.etat]} libelle={libelleDeliberation(d.etat)} />
                      {d.numero ? <span className="cc-mono">{d.numero}</span> : null}
                      {d.totaux ? <span>{`Pour ${d.totaux.pour}, contre ${d.totaux.contre}, abstention ${d.totaux.abstention}`}</span> : null}
                      {d.quorum_constate ? <span>{`Quorum constaté ${d.quorum_constate.compte} sur ${d.quorum_constate.requis}`}</span> : null}
                    </p>
                    {d.votes.length > 0 ? <p className={styles.detail}>Votes : {d.votes.map((x) => `${x.nom_complet} ${CHOIX_VOTE[x.choix as keyof typeof CHOIX_VOTE].toLowerCase()}${x.par_procuration ? ' (procuration)' : ''}`).join(' ; ')}</p> : null}
                    {d.motif_report ? <p className={styles.detail}>Report : {d.motif_report}</p> : null}
                    {d.texte_public ? <p className={styles.detail}>Texte public : {d.texte_public}</p> : null}
                    {d.objet_lie ? <p className={styles.detail}>Suivi d&apos;exécution : {d.objet_lie.visible ? `${d.objet_lie.libelle}, ${d.objet_lie.etat}${d.objet_lie.suivi ? `, ${d.objet_lie.suivi}` : ''}` : 'objet lié non visible avec vos droits'}{d.execution ? ` ; exécution constatée : ${d.execution.observation}` : ''}</p> : null}
                    <p className={styles.actes}>
                      {s.etat === 'ouverte' && d.etat === 'inscrite' && droits.modifier ? <Bouton variante="principal" onClick={() => onActe({ type: 'vote', id: d.id, objet: d.objet })}>Voter</Bouton> : null}
                      {['convoquee', 'ouverte'].includes(s.etat) && d.etat === 'inscrite' && droits.modifier ? <Bouton variante="discret" onClick={() => onActe({ type: 'report', id: d.id, objet: d.objet })}>Reporter</Bouton> : null}
                      {['adoptee', 'transmise', 'publiee'].includes(d.etat) && !d.objet_lie && droits.modifier ? <Bouton variante="secondaire" onClick={() => onActe({ type: 'lien', id: d.id, objet: d.objet })}>Lier un projet</Bouton> : null}
                      {d.etat === 'publiee' && d.objet_lie && droits.modifier ? <Bouton variante="secondaire" onClick={() => onActe({ type: 'execution', id: d.id, objet: d.objet })}>Constater l&apos;exécution</Bouton> : null}
                    </p>
                  </div>
                ) : <p className={styles.detail}>Point d&apos;information, sans délibération.</p>}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

export function SectionPv({ s, droits }: { s: SessionConseil; droits: DroitsConseils }) {
  const action = useAction({ executer: (q: 'president' | 'secretaire') => validerPv(s.id, q), succes: (r) => (r.etat === 'pv_valide' ? 'Procès-verbal validé par le président et le secrétaire.' : 'Validation enregistrée.'), invalider: INVALIDER_CONSEILS });
  if (!s.pv && !s.transmission) return null;
  const faites = new Set(s.pv?.validations.map((v) => v.qualite) ?? []);
  return (
    <section className={styles.bloc} aria-labelledby="titre-pv">
      <h2 id="titre-pv">Procès-verbal et transmission</h2>
      {s.pv ? <p className={styles.detail}>Procès-verbal : <Lien href={`/documents/${s.pv.document_id}`}>ouvrir le document</Lien></p> : null}
      {s.pv?.validations.map((v) => <p key={v.qualite} className={styles.detail}>{`Validé comme ${v.qualite === 'president' ? 'président' : 'secrétaire'} de séance${v.par ? ` par ${v.par}` : ''}`}</p>)}
      {s.etat === 'pv_redige' ? (
        <p className={styles.actes}>
          {!faites.has('secretaire') && droits.valider ? <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer('secretaire')}>Valider comme secrétaire de séance</Bouton> : null}
          {!faites.has('president') && droits.signer ? <Bouton variante="principal" chargement={action.enCours} onClick={() => action.lancer('president')}>Valider comme président de séance</Bouton> : null}
        </p>
      ) : null}
      {action.messageErreur ? <p role="alert" className={styles.negatif}>{action.messageErreur}</p> : null}
      {s.transmission ? (
        <p className={styles.detail}>
          {`Transmise le ${s.transmission.date} (${MODES_TRANSMISSION[s.transmission.mode]}, référence ${s.transmission.reference}) à ${s.transmission.destinataire}`}
          {s.transmission.accuse ? ` ; accusé de réception du ${s.transmission.accuse.date}` : ' ; accusé de réception attendu'}
        </p>
      ) : null}
      {s.publiee_le ? <p className={styles.detail}>{`Publiée le ${s.publiee_le.slice(0, 10)} sur le portail.`}</p> : null}
    </section>
  );
}
